import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { StatusDot } from "@/components/brand/status-dot";
import type { Availability, TenantInfo } from "../../ports.ts";
import type { BuilderConfig } from "../api.ts";
import { ErrorNote, errText, Field, formatWhen, SectionTitle } from "../ui.tsx";
import type { StageProps } from "./EmployeeShell.tsx";

export function AssignStage({ api, detail, reload }: StageProps) {
  const e = detail.employee;
  const [tenants, setTenants] = useState<TenantInfo[]>();
  const [history, setHistory] = useState<Availability[]>([]);
  const [config, setConfig] = useState<BuilderConfig>();
  const cur = detail.availability;
  const [mode, setMode] = useState<"all" | "tenants">(cur?.mode ?? "tenants");
  const [picked, setPicked] = useState<Set<string>>(new Set(cur?.tenantSlugs ?? []));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);
  const locked = detail.stages.assign === "locked";

  useEffect(() => {
    api.tenants().then(setTenants, (err) => setError(`The tenant directory could not be reached: ${errText(err)}`));
    api.availability(e.id).then(setHistory, () => {});
    api.config().then(setConfig, () => {});
  }, [e.id, detail.availability?.seq]);

  const toggle = (slug: string, on: boolean) => {
    const next = new Set(picked);
    on ? next.add(slug) : next.delete(slug);
    setPicked(next);
  };

  const latest = detail.releases[0];

  return (
    <div className="grid gap-4">
      <SectionTitle
        title="Assign"
        description="Choose which tenants see this employee in their marketplace. This is separate from the release: you can change it any time, and every change is kept. Tenants that already hired it keep their copy."
      />
      <ErrorNote error={error} />
      {locked && <p className="text-sm text-muted-foreground">Publish a release first.</p>}
      {latest && (
        <p className="text-sm">
          Tenants see <span className="font-medium">v{latest.version}</span>, the latest release.{" "}
          {cur ? (
            cur.mode === "all" ? (
              <StatusDot tone="success" label="Currently available to all tenants" />
            ) : cur.tenantSlugs.length ? (
              <StatusDot tone="success" label={`Currently available to ${cur.tenantSlugs.join(", ")}`} />
            ) : (
              <StatusDot tone="neutral" label="Currently available to no tenant" />
            )
          ) : (
            <StatusDot tone="neutral" label="Not assigned to any tenant yet" />
          )}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Availability</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <RadioGroup value={mode} onValueChange={(v) => setMode(v as "all" | "tenants")} className="grid gap-2">
            {config?.canAssignAll !== false && (
              <div className="flex items-center gap-2">
                <RadioGroupItem value="all" id="av-all" />
                <Label htmlFor="av-all">All tenants</Label>
              </div>
            )}
            <div className="flex items-center gap-2">
              <RadioGroupItem value="tenants" id="av-tenants" />
              <Label htmlFor="av-tenants">Specific tenants</Label>
            </div>
          </RadioGroup>

          {mode === "tenants" && (
            <div className="grid gap-2 rounded-lg border p-3">
              {!tenants && <p className="text-sm text-muted-foreground">Loading tenants…</p>}
              {tenants?.length === 0 && <p className="text-sm text-muted-foreground">No tenants are available to assign.</p>}
              {tenants?.map((t) => (
                <div key={t.slug} className="flex items-center gap-2">
                  <Checkbox id={`t-${t.slug}`} checked={picked.has(t.slug)} onCheckedChange={(v) => toggle(t.slug, Boolean(v))} />
                  <Label htmlFor={`t-${t.slug}`}>
                    {t.name} <span className="font-mono text-xs text-muted-foreground">{t.slug}</span>
                  </Label>
                </div>
              ))}
            </div>
          )}

          <Field label="Reason" htmlFor="av-reason" hint="Kept in the audit trail.">
            <Input id="av-reason" value={reason} maxLength={500} placeholder="Pilot with Acme support team" onChange={(x) => setReason(x.target.value)} />
          </Field>
          <div className="flex items-center gap-3">
            <Button
              disabled={busy || locked}
              onClick={async () => {
                setBusy(true);
                setError(undefined);
                setSaved(false);
                try {
                  await api.setAvailability(e.id, { mode, tenantSlugs: mode === "all" ? [] : [...picked], reason });
                  setReason("");
                  setSaved(true);
                  await reload();
                } catch (err) {
                  setError(errText(err));
                } finally {
                  setBusy(false);
                }
              }}
            >
              Save availability
            </Button>
            {saved && <StatusDot tone="success" label="Saved" />}
          </div>
        </CardContent>
      </Card>

      {history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>History</CardTitle>
            <CardDescription>Every availability change, newest first.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-2 text-sm">
            {history.map((h) => (
              <div key={h.id} className="flex flex-wrap gap-x-2">
                <span className="font-mono text-xs text-muted-foreground">#{h.seq}</span>
                <span className="font-medium">{h.mode === "all" ? "All tenants" : h.tenantSlugs.join(", ") || "No tenants"}</span>
                <span className="text-muted-foreground">
                  {formatWhen(h.createdAt)}
                  {h.reason ? ` · ${h.reason}` : ""}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
